import {
  AdminHeader,
  AdminSpinner,
  AdminSwitch,
  BrandComponent,
  BrandImageComponent,
  BrandRequestDetailsEntity,
  Button,
  ButtonSpinner,
  Disclaimer,
  Div,
  DragScrollX,
  H3,
  Img,
  LocationMode,
  P,
  SuccessToast,
  Tab,
  UnderlineHoverLink,
  activatingBrandRequest,
  approveBrandRequest,
  dateFormat,
  downloadBase64CSV,
  downloadBase64PDF,
  getBrandRequestDetails,
  lightTheme,
  queryKeys,
  renderPosImage,
  renderPosName,
  reportError,
} from '@vgrubs/components';
import {
  getBrandMenuCsv,
  getBrandMenuPdf,
} from 'components/src/api/brands.api';
import { useInvalidateQueries } from 'components/src/hooks/useInvalidateQueries';
import { BTMenuItemResponse } from 'components/src/types/brand.types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { OpenConversationButton } from '../Chat/OpenConversationButton';
import { BrandMenuItem } from './components/BrandMenuItem';
import { EditBrandItemModal } from './components/EditBrandItemModal';
import { KeyValueInfo } from './components/KeyValueInfo';
import { SectionCard } from './components/SectionCard';
import { RejectBrandModal } from './RejectBrandModal';

/**
 * One virtual brand request waiting for approval. Left: who asked and what they run on.
 * Right: the activating switch. Below, full width: the submitted menu to review and edit,
 * and the brands already active at that location. Same shape as RestaurantDetails.
 */
export const BrandVerificationDetails = () => {
  const [loading, setLoading] = useState(false);
  const [activatingLoading, setActivatingLoading] = useState(false);
  const [brand, setBrand] = useState<BrandRequestDetailsEntity | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [editItemModal, setEditItemModal] = useState<BTMenuItemResponse | null>(
    null
  );
  const navigate = useNavigate();

  const [rejectModal, setRejectModal] = useState<string | number | null>(null);

  const [activating, setActivating] = useState(false);

  const { id } = useParams() as { id: string };
  const { invalidateByKey } = useInvalidateQueries();
  async function handleVerifyBrand() {
    try {
      setLoading(true);
      const { data } = await approveBrandRequest(id);
      SuccessToast(data.message);
      invalidateByKey(queryKeys.admin.verifications.count);
      invalidateByKey(queryKeys.admin.verifications.brands);
      navigate(-1);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleGetBrandRequest() {
    try {
      setLoading(true);
      const { data } = await getBrandRequestDetails(id);
      setBrand(data.data);
      setActivating(data.data.activating);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleActivateBrand(e: React.ChangeEvent<HTMLInputElement>) {
    if (activating) return;
    if (activatingLoading) return;
    const activatingValue = e.target.checked;
    setActivating(activatingValue);
    try {
      setActivatingLoading(true);
      const { data } = await activatingBrandRequest(id, activatingValue);
      setBrand(data.data);
    } catch (error) {
      reportError(error);
      setActivating(brand?.activating ?? false);
    } finally {
      setActivatingLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      handleGetBrandRequest();
    }
  }, []);

  useEffect(() => {
    if (brand && selectedCategory === null) {
      setSelectedCategory(brand.menu?.categories[0]?.id ?? null);
    }
  }, [brand]);

  async function exportMenuPdf() {
    if (!brand) return;
    try {
      setLoading(true);
      const { data } = await getBrandMenuPdf(id);
      downloadBase64PDF(data.data, `Brand ${brand.name} Menu PDF`);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function exportMenuCsv() {
    if (!brand) return;
    try {
      setLoading(true);
      const { data } = await getBrandMenuCsv(id);
      downloadBase64CSV(data.data, `Brand ${brand.name} Menu CSV`);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const { name, owner } = brand || {};
  const categories = brand?.menu?.categories ?? [];
  const currentCategory = categories.find((c) => c.id === selectedCategory);

  return (
    <>
      <AdminHeader>
        <OpenConversationButton userId={owner?.id} role={owner?.role} />
      </AdminHeader>
      {loading && <AdminSpinner />}
      {brand && (
        <>
          <Div
            $flex
            $columnSM
            $gap="32px"
            $gapSM="20px"
            $m="32px 0"
            $mSM="20px 0"
            $between
          >
            <Div $flex $alignCenter $gap="20px" $gapSM="16px">
              <BrandImageComponent
                image={brand.imageUrl ?? ''}
                height={100}
                width={100}
              />
              <Div>
                <H3 $mb="8" $color={lightTheme.primary}>
                  {name}
                </H3>
                <UnderlineHoverLink to={`/locations/${brand.location.id}`}>
                  <P $mb="4">{brand.location.name}</P>
                </UnderlineHoverLink>
                <Disclaimer $color={lightTheme.lightGray} $m="0">
                  Request submitted: {dateFormat(brand.createdAt || '')}
                </Disclaimer>
              </Div>
            </Div>

            <Div
              $flex
              $alignEnd
              $justifyCenter
              $column
              $gap="16px"
              $widthSM="100%"
            >
              <Button
                $selected
                $gray
                $w="250px"
                $wSM="100%"
                onClick={() => setRejectModal(id)}
              >
                Reject Brand
              </Button>
              <Button
                $selected
                $w="250px"
                $wSM="100%"
                onClick={handleVerifyBrand}
              >
                Approve Brand
              </Button>
            </Div>
          </Div>

          <Div $flex $columnSM $gap="32px" $gapSM="24px">
            <Div $width="40%" $widthSM="100%" $flex $column $gap="20px">
              <P $m="0" $bold $color={lightTheme.lightGray}>
                Submitted by the restaurant
              </P>

              <SectionCard title="Contact">
                <Link to={`/users/${owner?.id}`}>
                  <KeyValueInfo
                    k="Owner"
                    v={owner?.firstName + ' ' + owner?.lastName}
                  />
                </Link>
                <a href={`tel:${owner?.phone?.number}`}>
                  <KeyValueInfo k="Phone" v={owner?.phone?.number ?? ''} last />
                </a>
              </SectionCard>

              {brand.location.mode === LocationMode.pos && (
                <SectionCard
                  title="POS"
                  action={
                    <Img
                      $height="40px"
                      src={renderPosImage(brand.posType ?? '')}
                      alt={renderPosName(brand.posType ?? '', 'POS')}
                    />
                  }
                >
                  <P $m="0" $color={lightTheme.darkGray}>
                    This location runs on a POS, so the menu has to be created
                    directly in it.
                  </P>
                </SectionCard>
              )}
            </Div>

            <Div $width="60%" $widthSM="100%" $flex $column $gap="20px">
              <P $m="0" $bold $color={lightTheme.lightGray}>
                Set up before approving
              </P>

              <SectionCard
                title="Activating"
                action={
                  <Div $flex $alignCenter $gap="10px" $relative>
                    {activatingLoading && (
                      <ButtonSpinner color={lightTheme.primary} right={-20} />
                    )}
                    <AdminSwitch
                      checked={activating}
                      onChange={handleActivateBrand}
                      disabled={activating}
                    />
                  </Div>
                }
              >
                <P $m="0" $color={lightTheme.darkGray}>
                  Once enabled, the client is notified that we are setting up
                  their brand and the menu they submitted. This cannot be
                  undone.
                </P>
              </SectionCard>
            </Div>
          </Div>

          <Div $flex $alignCenter $between $gap="16px" $wrap $mt="40" $mb="16">
            <P $m="0" $bold $color={lightTheme.lightGray}>
              Submitted menu
            </P>
            {brand.menu && (
              <Div $flex $alignCenter $gap="8px 16px" $wrap $justifyEnd>
                <Button
                  $small
                  $selected
                  $w="150px"
                  $wSM="130px"
                  onClick={exportMenuPdf}
                >
                  Export PDF
                </Button>
                <Button
                  $small
                  $selected
                  $w="150px"
                  $wSM="130px"
                  onClick={exportMenuCsv}
                >
                  Export CSV
                </Button>
              </Div>
            )}
          </Div>

          {categories.length > 0 && (
            <Div $flex $gap="16px" $mb="24" $mbSM="16" $width="100%" $wrap>
              {categories.map((category) => (
                <Tab
                  key={category.id}
                  $width="170px"
                  $widthSM="calc(33.3% - 12px)"
                  $minWidthAutoSM
                  selected={category.id === selectedCategory}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </Tab>
              ))}
            </Div>
          )}

          <Div
            $grid
            $gridTemplateColumns="repeat(2, 1fr)"
            $gridTemplateColumnsSM="repeat(1, 1fr)"
            $gap="8px 30px"
            $gapSM="8px"
            $width="100%"
            $mb="32"
          >
            {currentCategory?.items.map((item, index) => (
              <BrandMenuItem
                key={item.id ?? index}
                item={item}
                index={index}
                setEditItemModal={setEditItemModal}
              />
            ))}
          </Div>

          {currentCategory && currentCategory.items.length === 0 && (
            <P $m="40px 0" $center>
              No items in this category
            </P>
          )}

          {!brand.menu && (
            <P $m="40px 0" $center>
              No menu items
            </P>
          )}

          {brand.brands.length > 0 && (
            <>
              <P $m="32px 0 0 0" $bold $color={lightTheme.lightGray}>
                Brands already active at this location
              </P>
              <DragScrollX width="22%" widthMD="22%">
                {brand.brands.map((activeBrand) => (
                  <BrandComponent
                    key={activeBrand.id}
                    brand={activeBrand}
                    onClick={() => navigate(`/brand/${activeBrand.id}`)}
                  />
                ))}
              </DragScrollX>
            </>
          )}
        </>
      )}

      <RejectBrandModal
        modalIsOpen={rejectModal ?? ''}
        closeModal={() => setRejectModal(null)}
        setLoading={setLoading}
      />

      <EditBrandItemModal
        editItemModal={editItemModal}
        closeModal={() => setEditItemModal(null)}
        setLoading={setLoading}
        brandId={id}
        setEditItemModal={setEditItemModal}
        setBrand={setBrand}
      />
    </>
  );
};
